/**
 * Single source of truth for page FAQs.
 *
 * Both the visible <FaqSection> and the FAQPage JSON-LD in SeoManager read from
 * here. That coupling is deliberate: Google only allows FAQPage markup for content
 * that is visibly present on the page, so schema and copy must never drift apart.
 *
 * Answers are written answer-first - the direct answer in the opening sentence -
 * because generative engines (Copilot, ChatGPT, AI Overviews) lift these close to
 * verbatim and typically quote only the first sentence or two.
 */
export type FaqItem = { q: string; a: string };

const TENANT_ANSWER =
  'No. Meta-365 runs as a managed application inside your own Microsoft 365 tenant, so documents, emails and records stay in your existing SharePoint, Outlook and Planner. There is no migration to a vendor cloud and no new data silo to security-review.';

export const FAQ_BY_PATH: Record<string, FaqItem[]> = {
  '/solutions/pentities': [
    { q: 'Does my data leave my Microsoft 365 tenant?', a: TENANT_ANSWER },
    {
      q: 'What is pEntities?',
      a: 'pEntities is a unified business platform that runs inside Microsoft 365, combining document management, HR, legal, finance, ticketing and a no-code workflow designer in a single application rather than six separate tools.',
    },
    {
      q: 'Do users need to learn a new system?',
      a: 'No. pEntities is delivered through Microsoft Teams, SharePoint and Outlook, so staff work in the applications they already use every day. There is no separate portal to log into and no retraining programme.',
    },
    {
      q: 'How is pEntities licensed?',
      a: 'pEntities is licensed per tenant rather than per user, with usage-based components. A free tier is available, and the platform is transactable through Microsoft AppSource and Azure Marketplace, so spend can draw down an existing Microsoft commitment.',
    },
  ],

  '/solutions/hr-hub': [
    {
      q: 'Can employees book annual leave inside Microsoft Teams?',
      a: 'Yes. Staff request annual leave directly in Microsoft Teams, and approvals route to managers through the same interface. There is no separate HR portal for employees to log into.',
    },
    { q: 'Where is employee data stored?', a: TENANT_ANSWER },
    {
      q: 'What does the HR module cover beyond leave?',
      a: 'The HR module covers onboarding automation, performance reviews, contract-expiry alerts at 30, 60 and 90 days, training compliance, work-anniversary alerts and HR analytics, alongside leave and absence management.',
    },
    {
      q: 'How does it compare with standalone HR software?',
      a: 'Standalone HR applications store your employee records in the vendor cloud and are typically priced per employee. Meta-365 keeps HR data in your own tenant, prices per tenant, and includes HR as one of six modules rather than as a separate subscription.',
    },
  ],

  '/solutions/ticketing': [
    {
      q: 'Can I run a helpdesk inside Microsoft Teams?',
      a: 'Yes. Meta-365 provides a Teams-native service desk with per-category SLA clocks, five priority levels, auto-assignment, AI triage and SLA-breach escalation, all operated from within Microsoft Teams.',
    },
    {
      q: 'Can customers raise tickets without a Microsoft 365 account?',
      a: 'Yes. External customers submit and track tickets through a PIN-link portal that requires no Microsoft 365 account and no licence, while your agents work inside Teams.',
    },
    {
      q: 'How is ticketing priced compared with per-agent tools?',
      a: 'Meta-365 is priced per tenant rather than per agent, so the cost does not rise as you add service-desk staff. Most Teams ticketing tools charge per agent per month.',
    },
    { q: 'Where do tickets and attachments live?', a: TENANT_ANSWER },
  ],

  '/solutions/case-management': [
    {
      q: 'Can Meta-365 extract clauses from contracts automatically?',
      a: 'Yes. The legal module uses AI clause extraction to identify parties, dates, obligations and risk terms from uploaded contracts, and surfaces them for review rather than requiring manual reading.',
    },
    { q: 'Is contract data sent to an external AI service outside my tenant?', a: TENANT_ANSWER },
    {
      q: 'What else does the legal module handle?',
      a: 'The legal module covers full contract lifecycle management, hearing and deadline tracking, conflict-of-interest checks and legal analytics, in addition to AI clause extraction.',
    },
    {
      q: 'Does it replace our document management system?',
      a: 'It can. Contracts and matter files are stored in your existing SharePoint with version comparison, audit trail, redaction and retention policies applied, so a separate legal DMS is often unnecessary.',
    },
  ],

  '/solutions/automation': [
    {
      q: 'Is this an alternative to Power Automate?',
      a: 'It is a simpler, flat-priced alternative for business process automation. Meta-365 provides a visual no-code canvas with 15 node types, 30+ triggers and 42 action tools, including branching, loops, parallel paths and dry-run simulation, at one per-tenant price rather than per-user premium licensing.',
    },
    {
      q: 'We are on legacy SharePoint workflows. What are our options?',
      a: 'Microsoft retired SharePoint 2013 workflows on 2 April 2026 and InfoPath on 14 July 2026, so affected processes must be rebuilt. The Meta-365 Workflow Designer is a self-serve replacement that runs inside your existing Microsoft 365 tenant.',
    },
    {
      q: 'Do I need a developer to build workflows?',
      a: 'No. Workflows are built on a visual drag-and-drop canvas with real-time validation and dry-run simulation, so business users can design and test a process without writing expressions or code.',
    },
  ],

  '/solutions/docsearch': [
    {
      q: 'What can Meta-365 do that native SharePoint cannot?',
      a: 'Meta-365 adds word-level version comparison, permanent redaction, annotation, check-in/check-out with a full audit trail, records and retention management, and document-expiry alerts across multiple libraries, which native SharePoint does not provide out of the box.',
    },
    {
      q: 'Can I redact documents inside Microsoft 365?',
      a: 'Yes. Documents can be annotated and permanently redacted in place, with PurView sensitivity labels applied, without exporting the file to a third-party tool.',
    },
    { q: 'Does document content leave my tenant?', a: TENANT_ANSWER },
  ],

  '/solutions/finance': [
    {
      q: 'Can Meta-365 extract data from invoices automatically?',
      a: 'Yes. The finance module applies document intelligence to invoices, contracts, receipts and IDs, extracting structured fields for processing instead of manual data entry.',
    },
    { q: 'Where is financial document data processed?', a: TENANT_ANSWER },
  ],

  '/plans-pricing': [
    {
      q: 'Is there a free plan?',
      a: 'Yes. Meta-365 offers a free tier, with Standard, Business and Enterprise plans above it. Paid plans combine a platform fee with usage-based components.',
    },
    {
      q: 'Is Meta-365 priced per user?',
      a: 'No. Meta-365 is priced per tenant with usage-based components, so the platform fee does not increase simply because more staff use it.',
    },
    {
      q: 'Can we buy through Microsoft AppSource or Azure Marketplace?',
      a: 'Yes. Meta-365 applications are transactable on Microsoft AppSource and Azure Marketplace, so purchases can draw down an existing Microsoft Azure consumption commitment.',
    },
  ],
};
